/**
 * AccessModal — data access & controls as a proper centered popup
 * (2026-08-18 UX overhaul; replaces the cramped 270px anchored popover).
 * Body = the ONE shared AppAccessSheet (governance plan §6), enlarged and
 * enriched: per-class summaries + real example verbs + tool counts, and for
 * storage apps the folder-scope row. Presets stay the zero-decision path.
 */

import React from 'react';
import PanelModal from './PanelModal';
import AppAccessSheet from '../AppAccessSheet';
import { CAPABILITY_CLASS_SUMMARIES } from '../ComposioClient';
import type {
  AccessDetail,
  AccessTier,
  AccessProfile,
} from '../ComposioClient';
import panelTheme from '../panelTheme';
import { useMailPrivacySection } from './mailPrivacySectionDoor';

export interface AccessModalProps {
  slug: string;
  displayName: string;
  detail: AccessDetail | null;
  canManage: boolean;
  saving: boolean;
  /** Storage apps: current scope label ("folders: all" / "3 folders chosen"). */
  folderScopeLabel?: string | null;
  onOpenFolders?: () => void;
  onSelectTier: (tier: AccessTier) => void;
  onSaveProfile: (profile: AccessProfile) => void;
  onClose: () => void;
  /** The access fetch for this open failed — the sheet says so rather than
   *  reading "loading access…" forever (2026-08-25). */
  loadFailed?: boolean;
  /** Gmail and Outlook, with the lever on (../mailPrivacyLever.ts): the "what Prest0n may see in email" section,
   *  fetched on demand (asset graph G4b). */
  showMailPrivacy?: boolean;
}

/** The section once its download arrives; nothing until then (a failed download leaves it out, like any door). */
const MailPrivacySlot: React.FC = () => {
  const mod = useMailPrivacySection(true);
  return mod ? <mod.MailPrivacySection /> : null;
};

export const AccessModal: React.FC<AccessModalProps> = ({
  slug,
  displayName,
  detail,
  canManage,
  saving,
  folderScopeLabel,
  onOpenFolders,
  onSelectTier,
  onSaveProfile,
  onClose,
  loadFailed,
  showMailPrivacy,
}) => (
  <PanelModal
    onClose={onClose}
    width="min(560px, 94vw)"
    ariaLabel={`Access controls for ${displayName}`}
    testId={`access-modal-${slug}`}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
      <div style={{ fontWeight: 700, color: panelTheme.ink, fontSize: '1rem' }}>
        what can Prest0n do in {displayName}?
      </div>
      <button
        onClick={onClose}
        aria-label="close"
        style={{
          marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
          color: panelTheme.grayFaint, fontSize: '1.1rem', lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
    <div style={{ fontSize: '0.8rem', color: panelTheme.inkMuted, lineHeight: 1.5, marginBottom: '0.7rem' }}>
      pick a preset — or fine-tune exactly what&rsquo;s allowed. changes apply to the
      whole workspace&rsquo;s use of this app and take effect immediately.
    </div>
    <AppAccessSheet
      slug={slug}
      displayName={displayName}
      access={detail?.access ?? null}
      classCounts={detail?.classCounts ?? null}
      classExamples={detail?.classExamples ?? null}
      classSummaries={CAPABILITY_CLASS_SUMMARIES}
      folderScopeRow={
        folderScopeLabel && onOpenFolders
          ? { label: folderScopeLabel, onChange: onOpenFolders }
          : null
      }
      canManage={canManage}
      saving={saving}
      onSelectTier={onSelectTier}
      onSaveProfile={onSaveProfile}
      {...(loadFailed ? { loadFailed } : {})}
    />
    {showMailPrivacy && <MailPrivacySlot />}
  </PanelModal>
);

export default AccessModal;
